import { uniq } from 'lodash';
import type { Msg } from '../client';
import type { User } from '../sdk/types';
import { MsgPayloadType, MessageAction } from '../sdk/types';
import { type WasmSdkArgsMap } from '../sdk/types';
import { mergeMsg } from '../utils';
import { AbstractApi } from './Api';

export enum LoadMessagesDirection {
  Up = 0,
  Down,
  Center,
}

export interface LoadMessagesArgs {
  chat_id: string;
  anchor_sequence: number | [base: number, sub: number];
  direction: LoadMessagesDirection;
  limit?: number;
  abortSignal?: AbortSignal;
}

export interface SendMsgParams {
  chat_id: string;
  payload: string | Record<string, any>;
  payload_type?: MsgPayloadType;
  mentionIds?: string[];
  replyId?: string;
  isEditing?: boolean;
  action?: MessageAction;
  msg_ext?: string | Record<string, any>;
  file_name?: string;
  file_data?: Uint8Array;
  file_password?: string;
}

export type SendForwardMsgParams = {
  forwardUid: string;
  action?: MessageAction;
} & Pick<SendMsgParams, 'payload' | 'chat_id' | 'payload_type'>;

export class MessagesApi extends AbstractApi {
  loadMessages({ chat_id, anchor_sequence, limit, direction, abortSignal }: LoadMessagesArgs) {
    const [base, sub] = Array.isArray(anchor_sequence) ? anchor_sequence : [anchor_sequence, 0];
    return this.client.invoke(
      'loadMessages',
      [chat_id, BigInt(base), BigInt(sub), direction, BigInt(limit ?? 20)],
      abortSignal
    );
  }
  sendReadReceipts(chat_id: string, msgIds: string[]) {
    return this.client.invoke('sendReadReceipts', [chat_id, msgIds]);
  }
  /**
   * @deprecated
   * @see sendMsg
   */
  sendMessage(...args: WasmSdkArgsMap['sendMessage']) {
    return this.client.invoke('sendMessage', args);
  }
  async sendMsg({
    chat_id,
    payload,
    payload_type = MsgPayloadType.TEXT,
    mentionIds = [],
    replyId = '',
    isEditing = false,
    action = MessageAction.ENCRYPT,
    msg_ext = '',
    file_name = '',
    file_data = new Uint8Array([]),
    file_password = '',
  }: SendMsgParams) {
    const payload_ = typeof payload === 'string' ? payload : JSON.stringify(payload);
    const msg_ext_ = typeof msg_ext === 'string' ? msg_ext : JSON.stringify(msg_ext);

    if (isEditing) {
      await this.editRemoteMessage(chat_id, replyId, payload_, mentionIds);
      return;
    }

    return this.client.invoke('sendMessage', [
      chat_id,
      payload_,
      payload_type,
      action,
      replyId,
      mentionIds,
      msg_ext_,
      file_name,
      file_data,
      file_password,
    ]);
  }
  sendForwardMsg(params: SendForwardMsgParams) {
    return this.sendMsg({ ...params, msg_ext: { forwardUid: params.forwardUid } });
  }
  sendLocalMessage(chat_id: string, cli_msg_id: string) {
    return this.client.invoke('sendLocalMessage', [chat_id, cli_msg_id]);
  }
  saveLocalMessage(chat_id: string, payload: string, payload_type: number) {
    return this.client.invoke('saveLocalMessage', [chat_id, payload, payload_type]);
  }
  editLocalMessage(chat_id: string, cli_msg_id: string, payload: string) {
    return this.client.invoke('editLocalMessage', [chat_id, cli_msg_id, payload]);
  }
  editRemoteMessage(
    chat_id: string,
    message_id: string,
    payload: string,
    at_list_json: string[]
  ) {
    return this.client.invoke('editRemoteMessage', [chat_id, message_id, payload, at_list_json]);
  }
  /**
   * @name editEmojiReaction
   * @param opt_type 1: add, 2: remove
   */
  async editEmojiReaction(
    chat_id: string,
    msg_id: string,
    emoji_id: string,
    opt_type: 1 | 2,
    localUpdateParams?: {
      client_msg_id: string;
      user_id: string;
    }
  ) {
    if (localUpdateParams) {
      this.client.dispatchEvent('UpdatePartialMessage', {
        chat_id,
        cli_msg_id: localUpdateParams.client_msg_id,
        message_id: msg_id,
        reactions: [
          {
            emoji_id,
            opt_type,
            op_at: Date.now(),
            user_id: localUpdateParams.user_id,
          },
        ],
      });
    }

    const result = await this.client.invoke('editEmojiReaction', [
      chat_id,
      msg_id,
      emoji_id,
      opt_type,
    ]);

    return result;
  }
  resendMessageEdit(chat_id: string, message_id: string) {
    return this.client.invoke('resendMessageEdit', [chat_id, message_id]);
  }
  canCelSendLocalMessage(chat_id: string, cli_msg_id: string) {
    return this.client.invoke('canCelSendLocalMessage', [chat_id, cli_msg_id]);
  }
  getLuckyBoxGrabsByPacketNo(packet_no: string) {
    return this.client.invoke('getLuckyBoxGrabsByPacketNo', [packet_no]);
  }
  getFirstMessage(chat_id: string, abortSignal?: AbortSignal) {
    return this.client.invoke('getFirstMessage', [chat_id], abortSignal);
  }
  getLastMessage(chat_id: string, abortSignal?: AbortSignal) {
    return this.client.invoke('getLastMessage', [chat_id], abortSignal);
  }
  async getExpectReadAndReaders(
    chat_id: string,
    message_id: string,
    cb: (readUsers: User[], unreadUsers: User[], users: User[]) => void,
    abortSignal?: AbortSignal
  ) {
    const { expect_read_list, reader_list } = await this.client.invoke(
      'getExpectReadAndReaders',
      [chat_id, message_id],
      abortSignal
    );

    const { promise, removeListener } = this.client.Api.user.resolveUsersInfo(
      expect_read_list,
      (users) => {
        const readUsers: User[] = [];
        const unreadUsers: User[] = [];

        const set = new Set(reader_list.map((item) => item.user_id));

        users.forEach((user) => {
          if (set.has(user.user_id)) {
            readUsers.push(user);
          } else {
            unreadUsers.push(user);
          }
        });

        cb(readUsers, unreadUsers, users);
      }
    );

    await promise;

    return removeListener;
  }
  /**
   * @deprecated
   * @see getMsgByCliMsgId
   */
  getMessageByCliMsgId(chat_id: string, cli_msg_id: string, abortSignal?: AbortSignal) {
    return this.client.invoke('queryMessageByCliMsgId', [chat_id, cli_msg_id], abortSignal);
  }
  getMsgByCliMsgId(
    sessionId: string,
    cliMsgId: string,
    cb?: (msg: Msg | undefined, isDeleted: boolean) => void,
    abortSignal?: AbortSignal
  ) {
    const promise = this.client.invoke(
      'queryMessageByCliMsgId',
      [sessionId, cliMsgId],
      abortSignal
    );

    const removeListeners: Array<() => void> = [];

    if (cb) {
      promise.then((msg) => {
        if (msg) {
          cb(msg, false);
          removeListeners[removeListeners.length] = this.client.addEventListener(
            'UpdatePartialMessage',
            ({ detail }) => {
              const { chat_id, cli_msg_id, message_id } = detail!;
              if (
                sessionId === chat_id &&
                (cliMsgId === cli_msg_id || message_id === msg.message_id)
              ) {
                msg = mergeMsg(msg, detail);
                cb(msg, false);
              }
            }
          );

          removeListeners[removeListeners.length] = this.client.addEventListener(
            'AtMemberReadMessage',
            ({ detail: { chat_id, cli_msg_id, user_id } }) => {
              if (sessionId === chat_id && cliMsgId === cli_msg_id) {
                msg = { ...msg, at_read_list: uniq([...msg.at_read_list, user_id]) };
                cb(msg, false);
              }
            }
          );

          removeListeners[removeListeners.length] = this.client.addEventListener(
            'DeleteMessages',
            ({ detail }) => {
              const { chat_id, cli_msg_ids } = detail!;
              if (sessionId === chat_id && cli_msg_ids.includes(cliMsgId)) {
                cb(undefined, true);
              }
            },
            { once: true }
          );
        } else {
          cb(undefined, true);
        }
      });
    }

    return Object.assign(promise, {
      removeListener: () => {
        removeListeners.forEach((removeListener) => removeListener());
      },
    });
  }
  getUserMessageCliIds(chat_id: string, user_id: string, abortSignal?: AbortSignal) {
    return this.client.invoke('getUserMessageCliIds', [chat_id, user_id], abortSignal);
  }

  deleteMessages(args: {
    chat_id: string;
    cli_msg_ids: string[];
    delete_other_party: boolean;
    abortSignal?: AbortSignal;
  }) {
    return this.client.invoke(
      'deleteMessagesByCliMsgId',
      [args.chat_id, args.cli_msg_ids, args.delete_other_party],
      args.abortSignal
    );
  }
  messagesSearchByKey(
    key: string,
    chat_ids: string[],
    time_start: number,
    time_end: number,
    offset: number,
    limit: number,
    abortSignal?: AbortSignal
  ) {
    return this.client.invoke(
      'messagesSearchByKey',
      [key, chat_ids, BigInt(time_start), BigInt(time_end), BigInt(offset), BigInt(limit)],
      abortSignal
    );
  }
  clearMessages(chat_id: string, abortSignal?: AbortSignal) {
    return this.client.invoke('clearMessages', [chat_id], abortSignal);
  }
}
